import type {
  InfraOptionValues,
  ManifestComponentLayer,
  ManifestInfraOption,
  ResolvedTemplate,
} from './types';

/** Names and values that are safe to write into a `.bicepparam` file. */
const BICEP_IDENTIFIER = /^[A-Za-z]\w*$/;
const BICEP_STRING_VALUE = /^[\w-]+$/;

export const templateInfraOptions = (
  template: ResolvedTemplate | undefined,
): ManifestInfraOption[] => template?.manifest.spec.infraOptions ?? [];

/** The option that offers its choices in a stack layer's row, if any. */
export const layerInfraOption = (
  template: ResolvedTemplate | undefined,
  layer: ManifestComponentLayer,
) =>
  templateInfraOptions(template).find(
    option => option.layer === layer && option.choices,
  );

/** Options shown as add-on toggles rather than in a stack layer's row. */
export const addOnInfraOptions = (template: ResolvedTemplate | undefined) =>
  templateInfraOptions(template).filter(option => !option.choices);

export const infraOptionValue = (
  option: ManifestInfraOption,
  values: InfraOptionValues,
) => values[option.id] ?? option.default;

const isAllowedValue = (option: ManifestInfraOption, value: unknown) =>
  option.choices
    ? typeof value === 'string' &&
      option.choices.some(choice => choice.value === value)
    : typeof value === 'boolean';

/** Whether every option named in `requires` currently has one of its listed values. */
export const isInfraOptionOffered = (
  option: ManifestInfraOption,
  options: ManifestInfraOption[],
  values: InfraOptionValues,
) =>
  Object.entries(option.requires ?? {}).every(([id, allowed]) => {
    const other = options.find(candidate => candidate.id === id);
    return (
      other !== undefined &&
      allowed.includes(String(infraOptionValue(other, values)))
    );
  });

/**
 * The values to use with `template`: `overrides` first, then the current
 * values, each kept only when the template's option allows it, else the
 * option's default. Options the template doesn't declare are dropped.
 */
export const resolveInfraOptions = (
  template: ResolvedTemplate | undefined,
  current: InfraOptionValues,
  overrides: InfraOptionValues = {},
): InfraOptionValues => {
  const resolved: InfraOptionValues = {};
  for (const option of templateInfraOptions(template)) {
    const wanted = overrides[option.id] ?? current[option.id];
    resolved[option.id] = isAllowedValue(option, wanted)
      ? (wanted as string | boolean)
      : option.default;
  }
  return resolved;
};

/** `bicepParam` → value for every option the template declares. */
export const bicepParamsFor = (
  options: ManifestInfraOption[],
  values: InfraOptionValues,
) => {
  const params: Record<string, string | boolean> = {};
  for (const option of options) {
    const value = infraOptionValue(option, values);
    if (isAllowedValue(option, value)) params[option.bicepParam] = value;
  }
  return params;
};

const formatBicepValue = (value: string | boolean) =>
  typeof value === 'boolean' ? String(value) : `'${value}'`;

/**
 * Rewrites the `param <name> = …` line of each parameter. Parameters the file
 * doesn't declare, and names or values that aren't plain identifiers, are
 * left alone.
 */
export const setBicepParams = (
  content: string,
  params: Record<string, string | boolean>,
) => {
  let updated = content;
  for (const [name, value] of Object.entries(params)) {
    if (!BICEP_IDENTIFIER.test(name)) continue;
    if (typeof value === 'string' && !BICEP_STRING_VALUE.test(value)) continue;
    updated = updated.replace(
      new RegExp(`^param ${name} = .*$`, 'm'),
      `param ${name} = ${formatBicepValue(value)}`,
    );
  }
  return updated;
};
